import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  CircularProgress,
} from "@material-ui/core";
import DefaultLayout from "../layouts/DefaultLayout";
import LoggedInLayout from "../layouts/LoggedInLayout";
import { AccountContext } from "../store/AccountContext";
import { PlayerContext } from "../store/PlayerContext";
import { Link, Navigate, useParams } from "react-router-dom";
import PreviewVideoRow from "./Video/PreviewVideoRow";
import { Player } from "./common/player";
import Seo from "./common/Seo";
import { getTitleAndChannelFromVideoName } from "../utils/strings";
import PreviewCTA from "./Home/PreviewCTA";
import { Box as BoxV5 } from "@mui/material";
import { isMobile, isTablet } from "react-device-detect";
import GetAppCard, { EPISODE_GET_APP_LINE } from "./MyAccount/GetAppCard";

const useStyles = makeStyles((theme) => ({
  background: {
    width: "100%",
    flexGrow: 1,
    // @ts-ignore
    backgroundColor: theme.palette.background.ffNavy,
  },
  loading: {
    textAlign: "center",
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
  },
  largeTitle: {
    // @ts-ignore
    color: theme.palette.background.lightgray,
    fontSize: "1.6rem",
    marginBottom: theme.spacing(0.5),
    "@media (min-width:600px)": {
      fontSize: "2.4rem",
      lineHeight: "2.6rem",
    },
    "@media (min-width:960px)": {
      fontSize: "3rem",
      lineHeight: "3.2rem",
    },
  },
  episodeTitle: {
    // @ts-ignore
    color: theme.palette.background.upgradeAlt,
    marginBottom: theme.spacing(1),
    fontSize: "1.4rem",
  },
  bodyText: {
    color: "#bbb",
  },
  detailsContainer: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  detailsIcon: {
    display: "inline",
    // @ts-ignore
    color: theme.palette.background.ffGreen,
    marginRight: "4px",
    fontSize: "1.1rem",
    "@media (min-width:600px)": {
      fontSize: "1.2rem",
    },
    "@media (min-width:960px)": {
      fontSize: "1.2rem",
    },
  },
  detailsText: {
    display: "inline",
    // @ts-ignore
    color: theme.palette.common.darkgray,
    marginRight: theme.spacing(2),
    fontSize: "0.9rem",
  },
  bodyTextStrong: {
    fontSize: "1rem",
    fontWeight: 500,
    // @ts-ignore
    color: theme.palette.background.upgradeBG,
  },
  marginSpacing: {
    margin: "20px 0",
  },
}));

const PreviewVideo = () => {
  const classes = useStyles();

  const { loggedIn, userProfile } = useContext(AccountContext);

  const { id: videoId } = useParams<{ id: string }>();

  const {
    videoLoading,
    loadPreviewVideo,
    currentPreviewVideo,
    clearCurrentPreviewVideo,
  } = useContext(PlayerContext);

  useEffect(() => {
    return () => {
      clearCurrentPreviewVideo();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A wrong or out-of-date slug goes to the "this link has changed" page,
  // not a dead-end 404 (#888). Set only once the
  // load has finished, so a valid preview never redirects.
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setNotFound(false);
    if (videoId) {
      loadPreviewVideo(videoId).then((found) => {
        if (!cancelled && !found) {
          setNotFound(true);
        }
      });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  if (notFound) {
    return <Navigate to="/link-changed" replace />;
  }

  if (videoLoading) {
    return (
      <DefaultLayout>
        <div className={`${classes.background} ${classes.loading}`}>
          <CircularProgress color="primary" />
        </div>
      </DefaultLayout>
    );
  }

  function getEpisodeName(name: string): string {
    const { title } = getTitleAndChannelFromVideoName(name);
    return title;
  }

  const Layout = loggedIn ? LoggedInLayout : DefaultLayout;

  // #1184: a signed-in member can land here (Video.tsx sends a visitor it
  // doesn't yet see as signed in, and LoggedInLayout lets a Free Feed member
  // through on /preview/), so they get the same get-the-app card as on
  // /video-details/, placed as there: above the player on a computer, between
  // the player and the details on a phone or tablet. No trailer line: it
  // shows only when the API sends the Members Only trailer
  // (`membersOnlyResponse`), which this page doesn't load. Signed-out
  // visitors see no card.
  const phoneOrTablet = isMobile || isTablet;
  const showGetApp = loggedIn && !!userProfile?.memberStatus;
  const getAppCard = (
    <GetAppCard
      memberStatusId={userProfile?.memberStatus}
      placement="episode"
      size="slim"
      tone="dark"
      line={EPISODE_GET_APP_LINE}
    />
  );

  return (
    <Layout>
      <div className={classes.background}>
        {currentPreviewVideo ? (
          <>
            <Seo
              title={getEpisodeName(currentPreviewVideo.name)}
              description={
                currentPreviewVideo.description ||
                "FarmFLiX is an online video platform dedicated to delivering the best agricultural videos, using the latest technologies, to the farming community."
              }
              path={`/preview/${videoId}`}
              image={currentPreviewVideo.poster}
              type="video.other"
              video={{
                name: getEpisodeName(currentPreviewVideo.name),
                description: currentPreviewVideo.description,
                thumbnailUrl: currentPreviewVideo.poster,
                uploadDate: currentPreviewVideo.publishedAt,
                durationMs: currentPreviewVideo.duration,
              }}
            />
            <PreviewCTA />
            <Container>
              {showGetApp && !phoneOrTablet && (
                <BoxV5 sx={{ pt: 2 }}>{getAppCard}</BoxV5>
              )}
              <Grid container>
                <Grid item xs={12} sm={12} md={12}>
                  <Player
                    userId={undefined}
                    video={currentPreviewVideo}
                    type="preview"
                  />
                </Grid>
                {showGetApp && phoneOrTablet && (
                  <Grid item xs={12}>
                    {getAppCard}
                  </Grid>
                )}
                <Grid item xs={12}>
                  <PreviewVideoRow video={currentPreviewVideo} />
                </Grid>
              </Grid>
            </Container>
          </>
        ) : (
          <Container fixed>
            <Grid container>
              <Grid item xs={12} sm={12} md={12} alignItems="center">
                <Box my={10}>
                  <Typography variant="h1" className={classes.largeTitle}>
                    404
                  </Typography>
                  <Typography variant="h3" color="primary">
                    Page Not Found
                  </Typography>
                  <Link to="/videos" style={{ textDecoration: "none" }}>
                    <Button size="small" className={classes.marginSpacing}>
                      Go to Dashboard
                    </Button>
                  </Link>
                </Box>
              </Grid>
            </Grid>
          </Container>
        )}
      </div>
    </Layout>
  );
};

export default PreviewVideo;
